<template>
  <div class="text-area-standard-box">
    <div
      class="text-area-standard-box__label"
      :class="{ 'text-area-standard-box__label__disabled': disabled }"
    >
      {{ label }}
    </div>
    <div class="wrapper">
      <textarea
        ref="textAreaRef"
        v-model="inputValue"
        class="text-area-standard"
        :disabled="disabled"
        :class="{
          'text-area-standard__disabled': disabled,
          'text-area-standard__error': error,
          'text-area-standard__success': success
        }"
        :placeholder="placeHolderText"
        @blur="emits('emitBlur')"
        @input="emitInputValue"
      />
    </div>
    <div
      class="text-area-standard-box__help-text"
      :class="{
        'text-area-standard-box__help-text__disabled': disabled,
        'text-area-standard-box__help-text__error': error,
        'text-area-standard-box__help-text__success': success
      }"
    >
      {{ error ? errorMessage : helpText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue'
const props = defineProps({
  disabled: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  helpText: { type: String, default: '' },
  hideIconX: { type: Boolean, default: false },
  label: { type: String, default: '' },
  placeHolderText: { type: String, default: '' },
  prefilledText: { type: String, default: '' },
  success: { type: Boolean, default: false },
  infoText: { type: String, default: '' }
})
interface EMITS {
  (e: 'emitInput', inputValue: string | number): void
  (e: 'emitBlur'): void
}
const emits = defineEmits<EMITS>()
const textAreaRef = ref<HTMLElement | null>(null)
const inputValue = ref<string | number>('')
const prefilledText = ref<string>(props.prefilledText)
const showIconXValue = ref<string>('')

function emitInputValue() {
  emits('emitInput', inputValue.value)
}
function showIconX() {
  showIconXValue.value = 'show'
}
watch(inputValue, (newVal) => {
  if (typeof newVal === 'string' && newVal.length >= 1 && !props.hideIconX) showIconX()
})
if (prefilledText.value?.length) inputValue.value = prefilledText.value
watch(prefilledText, (newVal) => {
  if (newVal?.length) inputValue.value = newVal
})
onMounted(() => {
  if (prefilledText.value?.length) inputValue.value = prefilledText.value
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';
.wrapper {
  display: contents;
  position: relative;
}
.text-area-standard {
  @include font-mixins.desktop-bold-paragraph;
  @include mixins.scrollbar-vertical;
  background: variables.$color-pure-white;
  border-radius: 12px;
  border: 1px solid variables.$color-greyscale-secondary;
  color: variables.$color-greyscale-content;
  height: 128px;
  min-height: 50px;
  padding: 12px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  resize: vertical;
  width: 100%;
  &:focus {
    border: variables.$border-width-xsmall solid variables.$color-primary;
    outline: variables.$border-width-medium solid variables.$color-primary-light;
    // Outline workaround for iPhone/Safari
    @supports (-webkit-overflow-scrolling: touch) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
    // Outline workaround for iPad/Safari
    @media not all and (min-resolution: 0.001dpcm) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
  }
  &__error {
    border: variables.$border-width-xsmall solid variables.$color-error;
    &:focus {
      border: variables.$border-width-xsmall solid variables.$color-error;
      outline: variables.$border-width-medium solid variables.$color-error-outline;
    }
  }
  &__success {
    border: variables.$border-width-xsmall solid variables.$color-success;
  }
  &__disabled {
    background: variables.$color-greyscale-background;
    &:hover {
      cursor: not-allowed;
    }
  }
}
.text-area-standard-box {
  display: flex;
  flex-direction: column;
  gap: variables.$gap-large;
  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    width: fit-content;
    gap: variables.$gap-large;
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
  }
  &__help-text {
    @include font-mixins.desktop-bold-label;
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
    &__error {
      color: variables.$color-error;
    }
    &__success {
      color: variables.$color-success;
    }
  }
}
</style>
